import {LabOrder} from './data/lab-order';
import {UtilsService} from './utils.service';
import {Coating} from './data/coating';
import {LabCodeMapperService} from '../lab-code-mapper/lab-code-mapper.service';
import {LCatRefWithOrderNumberType} from './data/lens-catalog-api-item';
import {LabOrderBusinessService} from './lab-order-business.service';
import {CoatingTypes} from './data/coatingTypes';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import * as pdfMake from 'pdfmake/build/pdfmake.js';
import {catchError} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';
import {LensCatalogBusinessService} from './lens-catalog-business.service';
import {SpinnerService} from '../orders/search/spinner.service';
import {Lens} from './data/lens';


@Injectable()
export class PrintPdfService {
  lensColorsMap = new Map<string, String>();
  lensStyleMap = new Map<string, String>();

  constructor(private labCodeMapperService: LabCodeMapperService,
              private labOrderBusinessService: LabOrderBusinessService,
              private lensCatalogBusinessService: LensCatalogBusinessService,
              private coatingTypes: CoatingTypes,
              private spinnerService: SpinnerService) {
  }

  public setLensCatDataMaps(lensDetails: LCatRefWithOrderNumberType[]) {
    // Loop through lensDetails to get lens color for each labOrderNumber
    lensDetails.forEach(lens => {
      if (lens) {
        if (lens.hasOwnProperty('error')) {
          console.log('Failed to retrieve lens detail from lens catalog API');
        } else {

          this.lensColorsMap.set(lens.labOrderNumber, lens.legacyLensColor);
          this.lensStyleMap.set(lens.labOrderNumber, lens.productDescription);
        }
      } else {
          console.log('Failed to retrieve lens detail from lens catalog API');
      }
    });
  }

  getContent(selectedLabOrders: LabOrder[]) {
    const content = [];
    let pageCount = 0;
    const totalPages = selectedLabOrders.length;

    selectedLabOrders.forEach(labOrder => {
      pageCount++;
      content.push(this.createContent(labOrder, pageCount, totalPages));
    });

    return content;
  }

  createLabServiceReport(labOrderNumbers: string[]) {
    let content;
    let docDefinition;

    if (labOrderNumbers.length === 0) {
      return;
    }
    // get array of observables; one item per checked order
    const observablesLabOrders: Observable<LabOrder>[] = this.buildObservableArray(labOrderNumbers);
    // execute and process observables all at once
    Observable.forkJoin(observablesLabOrders)
      .subscribe(retrievedLabOrders => {

        // check whether laborder
        // dataArray holds the result of each observable after has resolved
        console.log(retrievedLabOrders);

        const selectedLabOrders = [];
        retrievedLabOrders.forEach(labOrder => {
          if (labOrder.hasOwnProperty('error')) {
            console.log('Failed to retrieve labOrder detail for order ' + labOrder.labOrderNumber);
          } else {
            selectedLabOrders.push(labOrder);
          }
        });

        // Getting lens color for all the order numbers
        const observableLCatRef: Observable<LCatRefWithOrderNumberType>[] = this.buildObservableArrayForLensColor(selectedLabOrders);

        Observable.forkJoin(observableLCatRef)
          .subscribe(resultArray => {
            this.setLensCatDataMaps(resultArray);
            content = this.getContent(selectedLabOrders);
            const printPdfService = this;
            docDefinition = {
              info: {
                title: 'LABSERVICEREPORT.pdf',
                subject: 'LABSERVICEREPORT.pdf'
              },
              pageSize: 'LETTER',
              pageOrientation: 'landscape',
              pageMargins: [40, 30, 40, 30],

              header: function (page, pages) {
                const labOrder = selectedLabOrders[page - 1];
                return {
                  height: 14,
                  text: printPdfService.buildReportTitle(labOrder),
                  style: 'titleHeader',
                  alignment: 'center'
                };
              },
              footer: function (page, pages) {
                const isBold = (pages > 1) ? true : false;
                return {
                  margin: [20, 0, 20, 0],
                  height: 30,
                  columns: [
                    {
                      alignment: 'left',
                      text: '----------Confidential -- Contains Protected Health Information',
                    },
                    {
                      alignment: 'center',
                      text: printPdfService.buildFooterTimestamp(),
                    }, {
                      alignment: 'right',
                      bold: isBold,
                      text: [
                        {text: page.toString()},
                        ' of ',
                        {text: pages.toString()}
                      ]
                    }]
                };
              },
              content: content,
              unbreakable: true,
              styles: {
                titleHeader: {
                  fontSize: 16,
                  bold: true,
                  margin: [0, 10, 0, 0],
                },
                tableHeader: {
                  bold: true,
                  fontSize: 13,
                  color: 'black'
                }
              },
              defaultStyle: {
                fontSize: 9,
                lineHeight: .9
              }
            };

            try {
              pdfMake.createPdf(docDefinition).open();
              this.spinnerService.setSpinnerFlag(false);
            } catch (e) {
              // check popup/ad blockers
              alert(e.toString());
              this.spinnerService.setSpinnerFlag(false);
            }
          }); // End of Observable.forkJoin(observables1)
      }); // End of Observable.forkJoin(observables)
  }


  // build array of observables; one item per checked order
  buildObservableArray(checkedOrders: string[]): Observable<LabOrder>[] {
    const observables: Observable<LabOrder>[] = [];
    checkedOrders.forEach(element => {
      observables.push(
        this.labOrderBusinessService.viewLabOrder(element.valueOf())
          .pipe(
            catchError(err => of({
              labOrderNumber: element.valueOf(), error: err
            })))
      );
    });
    return observables;
  }

  buildObservableArrayForLensColor(labOrders: LabOrder[]): Observable<LCatRefWithOrderNumberType>[] {

    const observables: Observable<LCatRefWithOrderNumberType>[] = [];
    labOrders.forEach(labOrder => {
      if (labOrder.lens && labOrder.lens.spectacleLensId) {
        observables.push(
          this.lensCatalogBusinessService.getLensDetailWithOrderNo(labOrder)
            .pipe(
              catchError(err => of({
                Error: 'Could not retrieve lenses detail from the catalog API', error: err
              })))
        );
      }
    });
    return observables;
  }

  createContent(labOrder: LabOrder, pageCount: number, totalPages: number) {

    labOrder.frame.status = this.labCodeMapperService.deduceFrameStatus(labOrder.frame.supplier);
    const tableData = {
      table: {
        widths: ['50%', '50%'],
        headerRows: 0,
        body: [
          [
            // RIGHT SIDE
            [
              this.buildSectionAuthInfo(labOrder),
              this.buildSectionDoctorInfo(labOrder),
              this.buildSectionServices(labOrder),
              this.buildSectionLabInfo(labOrder),
              this.buildSectionSpecialInstructionsVSP(labOrder),
              this.buildSectionLabUseOnly(labOrder)
            ],
            // LEFT SIDE
            [
              this.buildSectionLabRxInfo(labOrder),
              this.buildSectionLensesTints(labOrder),
              this.buildSectionFrame(labOrder),
              this.buildSectionMiscCoatings(labOrder),
              this.buildSectionSpecialInstructionsLab(labOrder)
            ]
          ]
        ]
      },
      layout: 'noBorders',
      pageBreak: 'after'
    };
    tableData.pageBreak = pageCount === totalPages ? '' : 'after';

    return [
      this.buildSectionPatientInfo(labOrder),
      tableData
    ];
  }

  buildSectionPatientInfo(labOrder: LabOrder) {
    const orderLabel = UtilsService.isPrivateOrder(labOrder) ?
      'Private Order' : 'Order';
    const labId = labOrder.labBilling.labId;
    const labOrderNum = labOrder.labOrderNumber;
    const patientName = labOrder.patient.lastName + ', ' + labOrder.patient.firstName;
    const doctorName = labOrder.provider.providerName;
    const invoiceNum = labOrder.labInvoiceNumber;
    const sentDate = this.buildDate(labOrder.serviceDate);
    const resubmitIndicator = labOrder.resubmitIndicator;
    const invoiceText = resubmitIndicator > 0 ? 'RESUBMIT' : 'Invoice';

    return [
      this.buildSectionTitle(''),
      {
        columns: [
          {
            width: '50%',
            table: {
              widths: ['50%', '50%'],
              body: [
                [
                  {
                    text: [
                      {text: 'Lab'},
                      ' ',
                      {text: labId, bold: true}
                    ],
                    alignment: 'center'
                  },
                  {
                    text: [
                      {text: orderLabel},
                      ' ',
                      {text: labOrderNum, bold: true},
                    ],
                    alignment: 'center'
                  },
                ],
              ]
            },
            layout: 'noBorders'
          },
          [
            {
              width: '50%',
              table: {
                headerRows: 0,
                widths: ['auto', 'auto', '*', 'auto'],
                body: [
                  [
                    {text: 'Patient', alignment: 'right'},
                    {text: patientName, alignment: 'left', bold: true},
                    {text: ''},
                    {text: ''}
                  ],
                  [
                    {text: 'Doctor', alignment: 'right'},
                    {text: doctorName, alignment: 'left', bold: true},
                    {text: ''},
                    {text: ''}
                  ],
                  [
                    {
                      text: [
                        {text: orderLabel},
                      ],
                      alignment: 'right'
                    },
                    {
                      text: [
                        {text: labOrderNum, bold: true},
                      ],
                      alignment: 'left'
                    },
                    {
                      text: [
                        {text: invoiceText},
                        ' ',
                        {text: invoiceNum, bold: true},
                      ],
                      alignment: 'center'
                    },
                    {
                      text: [
                        {text: 'Sent'},
                        ' ',
                        {text: sentDate, bold: true},
                      ],
                      alignment: 'right'
                    },
                  ],
                ]
              },
              layout: 'noBorders'
            }
          ]
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionAuthInfo(labOrder: LabOrder) {
    const lastName = labOrder.patient.lastName;
    const firstName = labOrder.patient.firstName;
    const comment = UtilsService.isPrivateOrder(labOrder) ?
      'Private Order' : '';
    const sentDate = this.buildDate(labOrder.orderSubmitDate);

    const memberInfo = {
      memberLabel: {text: '', alignment: 'right'},
      memberValue: {text: '', alignment: 'left'},
      idLabel: {text: '', alignment: 'right'},
      idValue: {text: '', alignment: 'left'},
      gridLabel: {text: '', alignment: 'right'},
      gridValue: {text: '', alignment: 'left'},
      benefitLabel: {text: '', alignment: 'right'},
      benefitValue: {text: '', alignment: 'left'},
      sentDateLabel: {text: '', alignment: 'right'},
      sentDateValue: {text: sentDate, alignment: 'left'}
    };

    if (!UtilsService.isPrivateOrder(labOrder)) {
      const memberName = lastName + ', ' + firstName;
      const gridCode = labOrder.gridCode;
      const benefitDesc = labOrder.insuredBenefitDescription;

      memberInfo.memberLabel.text = 'Member';
      memberInfo.memberValue.text = memberName;
      memberInfo.idLabel.text = 'ID';
      // The original VSP report showed the memberId here (idValue), not the insuredBenefitId.  The API doesn't
      // currently return a value for the memberId. As a placeholder for now, just show the field with no value.
      memberInfo.idValue.text = '';
      memberInfo.gridLabel.text = 'Grid';
      memberInfo.gridValue.text = gridCode;
      memberInfo.benefitLabel.text = 'Benefit';
      memberInfo.benefitValue.text = benefitDesc;
      memberInfo.sentDateLabel.text = ' ';
      memberInfo.sentDateValue.text = '';
    }

    return [
      this.buildSectionTitle('Authorization Information'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Patient', alignment: 'right'},
                  {text: lastName + ', ' + firstName, bold: true, alignment: 'left'},
                ],
                [
                  memberInfo.memberLabel,
                  memberInfo.memberValue
                ],
                [
                  memberInfo.idLabel,
                  memberInfo.idValue
                ],
                [
                  {text: 'Comment', alignment: 'right'},
                  {text: comment, bold: true, alignment: 'left'},
                ],
              ]
            },
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: memberInfo.sentDateLabel, alignment: 'right'},
                  {text: memberInfo.sentDateValue, bold: true, alignment: 'left'},
                ],
                [
                  memberInfo.gridLabel,
                  memberInfo.gridValue
                ],
                [
                  memberInfo.benefitLabel,
                  memberInfo.benefitValue
                ],
              ]
            },
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionDoctorInfo(labOrder: LabOrder) {
    const officeName = this.notUndef(labOrder.account.physicalAddress.shortDescription);
    const providerName = this.notUndef(labOrder.provider.providerName);
    const phone = this.notUndef(labOrder.account.officePhone);
    const fax = this.notUndef(labOrder.account.physicalAddress.fax);
    const address1 = this.notUndef(labOrder.account.physicalAddress.addressLineOne);
    const address2 = this.notUndef(labOrder.account.physicalAddress.city);
    const address3 = this.notUndef(labOrder.account.physicalAddress.stateCode) + '  ' +
      this.notUndef(labOrder.account.physicalAddress.zip5) +
      this.notUndef(labOrder.account.physicalAddress.zipPlus4);
    const privateAccountNumber = this.notUndef(labOrder.privateAccountNumber);
    const npi = this.notUndef(labOrder.provider.renderingDoctorId);

    let privateAccountNumberSection = [
      {text: '', alignment: 'right'},
      {text: '', alignment: 'left', bold: true}
    ];
    if (UtilsService.isPrivateOrder(labOrder)) {
      privateAccountNumberSection = [
        {text: 'Private\nAcct #', alignment: 'right'},
        {text: privateAccountNumber, alignment: 'left', bold: true}
      ];
    }

    return [
      this.buildSectionTitle('Doctor Information'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Office', alignment: 'right'},
                  {text: officeName, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Doctor', alignment: 'right'},
                  {text: providerName, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Phone', alignment: 'right'},
                  {text: phone, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Address', alignment: 'right'},
                  {text: address1, alignment: 'left', bold: true},
                ],
                [
                  {text: '', alignment: 'right'},
                  {text: address2, alignment: 'left', bold: true},
                ],
                [
                  {text: '', alignment: 'right'},
                  {text: address3, alignment: 'left', bold: true},
                ],
                [
                  {text: '', alignment: 'right'},
                  {text: '', alignment: 'left', bold: true},
                ],
                [
                  {text: '', alignment: 'right'},
                  {text: '', alignment: 'left', bold: true},
                ],
                privateAccountNumberSection
              ]
            }
          },
          {
            // star-sized columns fill the remaining space
            // if there's more than one star-column, available width is divided equally
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: 'Fax', alignment: 'right'},
                  {text: fax, alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionServices(labOrder: LabOrder) {
    if (UtilsService.isPrivateOrder(labOrder)) {
      return {};
    }

    const dummy = '';
    const serviceDate = labOrder.serviceDate ?
      this.buildDate(labOrder.serviceDate) : '';

    return [
      this.buildSectionTitle('Services'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Date', alignment: 'right'},
                  {text: serviceDate, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Exam', alignment: 'right'},
                  {text: dummy, alignment: 'left', bold: true},
                ],
                [
                  {text: 'EMM', alignment: 'right'},
                  {text: dummy, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: '', alignment: 'right'},
                  {text: '', alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },

    ];
  }

  buildSectionLabInfo(labOrder: LabOrder) {
    const labName = this.notUndef(labOrder.labBilling.labName);
    const status = this.notUndef(this.labCodeMapperService.getOrderStatusDesc(labOrder.status));

    return [
      this.buildSectionTitle('Lab Information'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Name', alignment: 'right'},
                  {text: labName, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Status', alignment: 'right'},
                  {text: status, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: '', alignment: 'right'},
                  {text: '', alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionSpecialInstructionsVSP(labOrder: LabOrder) {
    if (UtilsService.isPrivateOrder(labOrder)) {
      return {};
    }

    // only show instructions when uncut-lens is selected.
    const vspInstructions = labOrder.lens.uncutLensSelected ? 'Uncut Lens' : '';

    return [
      this.buildSectionTitle('Special Instructions to VSP'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: '', alignment: 'right'},
                  {text: vspInstructions, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: '', alignment: 'right'},
                  {text: '', alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionSpecialInstructionsLab(labOrder: LabOrder) {
    let labInstructions = labOrder.labSpecialInstruction ? labOrder.labSpecialInstruction : '';

    if (labOrder.labSpecialInstructionType && labOrder.labSpecialInstructionType === 'REDO') {
      labInstructions = ' Redo  ' + labOrder.redoOrderNumber + '.  ' + labInstructions;
    }

    if (labOrder.labSpecialInstructionType && labOrder.labSpecialInstructionType === 'BACKSIDE') {
      labInstructions = ' Backside A/R only. ' + labInstructions;
    }

    // only show when uncut-lens is selected.
    if (labOrder.lens.uncutLensSelected) {
      labInstructions = 'Uncut Lens - ' + labInstructions;
    }

    return [
      this.buildSectionTitle('Special Instructions to Lab'),
      {text: labInstructions, alignment: 'left', bold: true}
    ];
  }

  buildSectionLabUseOnly(labOrder: LabOrder) {
    if (!UtilsService.isPrivateOrder(labOrder)) {
      return {};
    }

    const lineFill = '_______________';

    return [
      this.buildSectionTitle('Lab Use Only'),
      {
        columns: [
          {
            width: '33%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Lenses', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Temper', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Tax', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '33%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Frame', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Coating', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'S & H', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '34%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: 'Tint', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Misc', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Total', alignment: 'right'},
                  {text: lineFill, alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionLabRxInfo(labOrder: LabOrder) {
    const sphereRight = this.zeroPadValueToHundredths(labOrder.prescription.sphereRight);
    const cylinderRight = this.zeroPadValueToHundredths(labOrder.prescription.cylinderRight);
    const axisRight = labOrder.prescription.axisRight;
    let hPrismInRight = '';
    let hPrismOutRight = '';
    let vPrismInRight = '';
    let vPrismOutRight = '';

    const sphereLeft = this.zeroPadValueToHundredths(labOrder.prescription.sphereLeft);
    const cylinderLeft = this.zeroPadValueToHundredths(labOrder.prescription.cylinderLeft);
    const axisLeft = labOrder.prescription.axisLeft;
    let hPrismInLeft = '';
    let hPrismOutLeft = '';
    let vPrismInLeft = '';
    let vPrismOutLeft = '';

    const addRight = labOrder.prescription.addRight ? labOrder.prescription.addRight.toFixed(2) : '';
    const segmentHeightRight = this.zeroPadValueToHundredths(labOrder.prescription.segmentHeightRight);
    const ocHeightRight = this.zeroPadValueToHundredths(labOrder.prescription.opticalCenterHeightRight);
    const baseCurveLeft = labOrder.lens.baseCurveLeft ? labOrder.lens.baseCurveLeft.toFixed(2) : '';

    const addLeft = labOrder.prescription.addLeft ? labOrder.prescription.addLeft.toFixed(2) : '';
    const segmentHeightLeft = this.zeroPadValueToHundredths(labOrder.prescription.segmentHeightLeft);
    const ocHeightLeft = this.zeroPadValueToHundredths(labOrder.prescription.opticalCenterHeightLeft);
    const baseCurveRight = labOrder.lens.baseCurveRight ? labOrder.lens.baseCurveRight.toFixed(2) : '';

    const wrapAngle = labOrder.prescription.wrapAngle;
    const pantoscopicTilt = labOrder.prescription.pantoscopicTilt;
    const vertex = labOrder.prescription.backVertexDistance;

    let measurementType;
    let pdDistantRight = '';
    let pdNearRight = '';
    let pdDistantLeft = '';
    let pdNearLeft = '';
    if (labOrder.prescription.pdMeasurementType === 'BINOCULAR') {
      measurementType = 'Binoc';
      pdDistantRight = labOrder.prescription.pdBinocularDistant ?
        this.zeroPadValueToTenths(labOrder.prescription.pdBinocularDistant.toString()) : '';
      pdNearRight = labOrder.prescription.pdBinocularNear ?
        this.zeroPadValueToTenths(labOrder.prescription.pdBinocularNear.toString()) : '';
    } else {
      measurementType = 'MonStd';
      pdDistantRight = labOrder.prescription.pdMonocularDistantRight ?
        this.zeroPadValueToTenths(labOrder.prescription.pdMonocularDistantRight.toString()) : '';
      pdNearRight = labOrder.prescription.pdMonocularNearRight ?
        this.zeroPadValueToTenths(labOrder.prescription.pdMonocularNearRight.toString()) : '';
      pdDistantLeft = labOrder.prescription.pdMonocularDistantLeft ?
        this.zeroPadValueToTenths(labOrder.prescription.pdMonocularDistantLeft.toString()) : '';
      pdNearLeft = labOrder.prescription.pdMonocularNearLeft ?
        this.zeroPadValueToTenths(labOrder.prescription.pdMonocularNearLeft.toString()) : '';
    }

    if (labOrder.prescription.horizontalPrismDirectionRight && labOrder.prescription.horizontalPrismMeasurementRight) {
      if (labOrder.prescription.horizontalPrismDirectionRight === 'IN') {
        hPrismInRight = labOrder.prescription.horizontalPrismMeasurementRight.toFixed(2);
      } else {
        hPrismOutRight = labOrder.prescription.horizontalPrismMeasurementRight.toFixed(2);
      }
    }

    if (labOrder.prescription.horizontalPrismDirectionLeft && labOrder.prescription.horizontalPrismMeasurementLeft) {
      if (labOrder.prescription.horizontalPrismDirectionLeft === 'IN') {
        hPrismInLeft = labOrder.prescription.horizontalPrismMeasurementLeft.toFixed(2);
      } else {
        hPrismOutLeft = labOrder.prescription.horizontalPrismMeasurementLeft.toFixed(2);
      }
    }

    if (labOrder.prescription.verticalPrismDirectionRight && labOrder.prescription.verticalPrismMeasurementRight) {
      if (labOrder.prescription.verticalPrismDirectionRight === 'UP') {
        vPrismInRight = labOrder.prescription.verticalPrismMeasurementRight.toFixed(2);
      } else {
        vPrismOutRight = labOrder.prescription.verticalPrismMeasurementRight.toFixed(2);
      }
    }

    if (labOrder.prescription.verticalPrismDirectionLeft && labOrder.prescription.verticalPrismMeasurementLeft) {
      if (labOrder.prescription.verticalPrismDirectionLeft === 'UP') {
        vPrismInLeft = labOrder.prescription.verticalPrismMeasurementLeft.toFixed(2);
      } else {
        vPrismOutLeft = labOrder.prescription.verticalPrismMeasurementLeft.toFixed(2);
      }
    }


    return [
      this.buildSectionTitle('Lab and Prescription Information'),
      {
        layout: 'noBorders',
        headerRows: 0,
        table: {
          widths: ['*', '*', '*', '*', '*', '*', '*', '*'],
          body: [
            [
              {text: '', alignment: 'center'},
              {text: '', alignment: 'center'},
              {text: '', alignment: 'center'},
              {text: '', alignment: 'center'},
              {text: 'H Prism', alignment: 'center', colSpan: 2},
              {},
              {text: 'V Prism', alignment: 'center', colSpan: 2},
              {},
            ],
            [
              {text: '', alignment: 'center'},
              {text: 'Sphere', alignment: 'center'},
              {text: 'Cyl', alignment: 'center'},
              {text: 'Axis', alignment: 'center'},
              {text: 'In', alignment: 'center'},
              {text: 'Out', alignment: 'center'},
              {text: 'Up', alignment: 'center'},
              {text: 'Down', alignment: 'center'},
            ],
            [
              {text: 'Right', alignment: 'center'},
              {text: sphereRight, alignment: 'center', bold: true},
              {text: cylinderRight, alignment: 'center', bold: true},
              {text: axisRight, alignment: 'center', bold: true},
              {text: hPrismInRight, alignment: 'center', bold: true},
              {text: hPrismOutRight, alignment: 'center', bold: true},
              {text: vPrismInRight, alignment: 'center', bold: true},
              {text: vPrismOutRight, alignment: 'center', bold: true},
            ],
            [
              {text: 'Left', alignment: 'center'},
              {text: sphereLeft, alignment: 'center', bold: true},
              {text: cylinderLeft, alignment: 'center', bold: true},
              {text: axisLeft, alignment: 'center', bold: true},
              {text: hPrismInLeft, alignment: 'center', bold: true},
              {text: hPrismOutLeft, alignment: 'center', bold: true},
              {text: vPrismInLeft, alignment: 'center', bold: true},
              {text: vPrismOutLeft, alignment: 'center', bold: true},
            ],
            [
              {text: '', alignment: 'center'},
              {text: '', alignment: 'center'},
              {text: 'Bottom', alignment: 'center'},
              {text: 'Bottom', alignment: 'center'},
              {text: measurementType, alignment: 'center', colSpan: 2},
              {},
              {text: '', alignment: 'center'},
              {text: '', alignment: 'center'},
            ],
            [
              {text: '', alignment: 'center'},
              {text: 'Add', alignment: 'center'},
              {text: 'Seg Ht', alignment: 'center'},
              {text: 'OC Ht', alignment: 'center'},
              {text: 'Dist', alignment: 'center'},
              {text: 'Near', alignment: 'center'},
              {text: 'BaseCurve', alignment: 'center', colSpan: 2},
              {},
            ],
            [
              {text: 'Right', alignment: 'center'},
              {text: addRight, alignment: 'center', bold: true},
              {text: segmentHeightRight, alignment: 'center', bold: true},
              {text: ocHeightRight, alignment: 'center', bold: true},
              {text: pdDistantRight, alignment: 'center', bold: true},
              {text: pdNearRight, alignment: 'center', bold: true},
              {text: baseCurveRight, alignment: 'center', bold: true, colSpan: 2},
              {},
            ],
            [
              {text: 'Left', alignment: 'center'},
              {text: addLeft, alignment: 'center', bold: true},
              {text: segmentHeightLeft, alignment: 'center', bold: true},
              {text: ocHeightLeft, alignment: 'center', bold: true},
              {text: pdDistantLeft, alignment: 'center', bold: true},
              {text: pdNearLeft, alignment: 'center', bold: true},
              {text: baseCurveLeft, alignment: 'center', bold: true, colSpan: 2},
              {},
            ]
          ]
        }
      },
      {
        table: {
          headerRows: 0,
          widths: ['*', '*', '*'],
          body: [
            [
              {
                text: 'Position of Wear Measurements',
                alignment: 'center',
                colSpan: 3
              },
              {},
              {}
            ],
            [
              {
                text: [
                  {text: 'Frame Wrap'},
                  ' ',
                  {text: wrapAngle, bold: true}
                ],
                alignment: 'left'
              },
              {
                text: [
                  {text: 'Pantoscopic Tilt'},
                  ' ',
                  {text: pantoscopicTilt, bold: true}
                ],
                alignment: 'left'
              },
              {
                text: [
                  {text: 'Vertex'},
                  ' ',
                  {text: vertex, bold: true}
                ],
                alignment: 'right'
              },
            ],
          ]
        },
        layout: 'noBorders'
      },
    ];
  }

  buildSectionLensesTints(labOrder: LabOrder) {
    const material = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getMaterialTypeDesc(labOrder.lens.materialCode)));
    const type = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getVisionTypeDesc(labOrder.lens.visionTypeCode)));
    const color = this.lensColorsMap.get(labOrder.labOrderNumber) ? this.lensColorsMap.get(labOrder.labOrderNumber) : '';
    const style = this.labCodeMapperService.getDisplayableLensStyleDescription(labOrder.lens.spectacleLensDescription,
      this.lensStyleMap.get(labOrder.labOrderNumber));
    const tintColor = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getTintColorDesc(labOrder.lens)));
    const tintSample = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getTintSampleDesc(labOrder.lens.tintSampleCode)));
    const tintType = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getTintTypeDesc(labOrder.lens.tintTypeCode)));

    let rightTableWidth = '25%';
    if ((tintColor && tintColor.length > 0) ||
      (tintSample && tintSample.length > 0) ||
      (tintType && tintType.length > 0)) {
      rightTableWidth = 'auto';
    }

    return [
      this.buildSectionTitleMulti('Lenses', 'Tints'),
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Material', alignment: 'right'},
                  {text: material, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Type', alignment: 'right'},
                  {text: type, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Color', alignment: 'right'},
                  {text: color, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Style', alignment: 'right'},
                  {text: style, alignment: 'left', bold: true},
                ]
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', rightTableWidth],
              body: [
                [
                  {text: 'Color', alignment: 'right'},
                  // {text: "&nbsp;&nbsp;&nbsp;&nbsp;", alignment: 'left', bold: true, minWidth: 10},
                  {text: tintColor, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Sample', alignment: 'right'},
                  {text: tintSample, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Type', alignment: 'right'},
                  {text: tintType, alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionFrame(labOrder: LabOrder) {
    const dummy = '';
    const supplier = UtilsService.lowerCaseAllWordsExceptFirstLetters(this.notUndef(labOrder.frame.supplier));
    const manufacturer = this.notUndef(labOrder.frame.manufacturer);
    const model = this.notUndef(labOrder.frame.model);
    const collection = this.notUndef(labOrder.frame.collection);
    const eyeSize = this.notUndef(labOrder.frame.eyeSize);
    const dbl = this.notUndef(labOrder.frame.dbl);
    const temple = this.notUndef(labOrder.frame.temple);
    const status = this.notUndef(labOrder.frame.status);
    const materialType = UtilsService.lowerCaseAllWordsExceptFirstLetters(this.notUndef(labOrder.frame.materialType));
    const shape = this.labCodeMapperService.getShapeNames(this.notUndef(labOrder.frame.shape));
    const color = this.notUndef(labOrder.frame.color);
    const b = this.zeroPadValueToTenths(this.notUndef(labOrder.frame.b).toString());
    const ed = this.zeroPadValueToTenths(this.notUndef(labOrder.frame.ed).toString());
    const upc = this.notUndef(labOrder.frame.upc);
    const sku = this.notUndef(labOrder.frame.sku);

    let frameLabel = '';
    let frameValue = '';
    if (!UtilsService.isPrivateOrder(labOrder)) {
      frameLabel = 'Frame  ';
      frameValue = dummy;
    }
    let costValue = 0;
    let costValueStr = '';
    let costLabel = '';
    if (!UtilsService.isPrivateOrder(labOrder)) {
      costLabel = 'Cost';
      if (labOrder.frame.wholesaleCost) {
        costValue = labOrder.frame.wholesaleCost;
      }
      costValueStr = costValue.toFixed(2);
    }

    let retailValue = 0;
    let retailValueStr = '';
    let retailLabel = '';
    if (!UtilsService.isPrivateOrder(labOrder)) {
      retailLabel = 'Retail';
      if (labOrder.frame.retailCost) {
        retailValue = labOrder.frame.retailCost;
      }
      retailValueStr = retailValue.toFixed(2);
    }

    const costSection = {costLabel: costLabel, costValue: costValueStr};
    const retailSection = {retailLabel: retailLabel, retailValue: retailValueStr};

    return [
      this.buildSectionTitle('Frame'),
      {
        width: '50%',
        layout: 'noBorders',
        table: {
          headerRows: 0,
          widths: ['auto', 'auto', '*', 'auto', 'auto'],
          body: [
            [
              [
                {text: 'Supplier', alignment: 'left'},
              ],
              [
                {text: supplier, alignment: 'left', bold: true},
              ],
              {
                text: [
                  {text: frameLabel, alignment: 'center', bold: true},
                  {text: frameValue, alignment: 'center', bold: true},
                ],
                alignment: 'center'
              },
              [
                {text: 'Status', alignment: 'right'},
              ],
              [
                {text: status, alignment: 'left', bold: true},
              ]
            ]
          ]
        }
      },
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Manuf', alignment: 'right'},
                  {text: manufacturer, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Collect', alignment: 'right'},
                  {text: collection, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Name', alignment: 'right'},
                  {text: model, alignment: 'left', bold: true},
                ],
                [
                  {
                    text: [
                      {text: 'Eye'},
                      ' ',
                      {text: eyeSize, bold: true},
                      '   ',
                      {text: 'DBL'},
                      ' ',
                      {text: dbl, bold: true},
                      '   ',
                      {text: 'Temple'},
                      ' ',
                      {text: temple, bold: true},
                      '   '
                    ],
                    alignment: 'center',
                    colSpan: 2
                  },
                  // {text: dummy, alignment: 'left', bold: true},
                ],
                [
                  {text: 'SKU', alignment: 'right'},
                  {text: sku, alignment: 'left', bold: true},
                ],
                [
                  {text: costSection.costLabel, alignment: 'right'},
                  {text: costSection.costValue, alignment: 'left', bold: true},
                ]
                // costLine
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', 'auto'],
              body: [
                [
                  {text: 'Material', alignment: 'right'},
                  {text: materialType, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Shape', alignment: 'right'},
                  {text: shape, alignment: 'left', bold: true},
                ],
                [
                  {text: 'Color', alignment: 'right'},
                  {text: color, alignment: 'left', bold: true},
                ],
                [
                  {
                    text: [
                      {text: 'B'},
                      ' ',
                      {text: b, bold: true},
                      '   ',
                      {text: 'ED'},
                      ' ',
                      {text: ed, bold: true}
                    ],
                    alignment: 'center',
                    colSpan: 2
                  },
                  // {text: dummy, alignment: 'left', bold: true},
                ],
                [
                  {text: 'UPC', alignment: 'right'},
                  {text: upc, alignment: 'left', bold: true},
                ],
                [
                  {text: retailSection.retailLabel, alignment: 'right'},
                  {text: retailSection.retailValue, alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  buildSectionMiscCoatings(labOrder: LabOrder) {
    const dummy = '';
    const bevel = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getBevelDesc(labOrder.lens)));
    const edge = this.stripUnderscores(this.notUndef(this.labCodeMapperService.getEdgeDesc(labOrder.lens)));
    const thickness = this.labCodeMapperService.getThicknessDesc(this.notUndef(labOrder.prescription.thicknessTypeCode));

    const summary = '' +
      (labOrder.lens.antiReflectiveCoating && labOrder.lens.antiReflectiveCoating.description ?
        this.labCodeMapperService.getCoatingDescription(this.coatingTypes.AR, labOrder.lens.antiReflectiveCoating) + '; ' : '') +
      (labOrder.lens.scratchCoating && labOrder.lens.scratchCoating.description ?
        this.labCodeMapperService.getCoatingDescription(this.coatingTypes.SCRATCH, labOrder.lens.scratchCoating) + '; ' : '') +
      (labOrder.lens.glassCoatingOrTintType && labOrder.lens.glassCoatingOrTintType.description ?
        this.labCodeMapperService.getCoatingDescription(this.coatingTypes.GLASS, labOrder.lens.glassCoatingOrTintType) + '; ' : '') +
      (labOrder.lens.mirrorSkiCoating && labOrder.lens.mirrorSkiCoating.description ?
        this.labCodeMapperService.getCoatingDescription(this.coatingTypes.MIRROR, labOrder.lens.mirrorSkiCoating) + '; ' : '') +
      (labOrder.lens.uvCoating && labOrder.lens.uvCoating.description ?
        this.labCodeMapperService.getCoatingDescription(this.coatingTypes.UV, labOrder.lens.uvCoating) + '; ' : '') +
      (labOrder.lens.tintColorDescription ?
        this.stripUnderscores(this.notUndef(this.labCodeMapperService.getTintColorDesc(labOrder.lens))) + '; ' : '') +
      (!UtilsService.isPrivateOrder(labOrder) && labOrder.lens.billingOptionCodes ?
        this.labCodeMapperService.getLensEnhancementDescriptions(this.labOrderBusinessService.getLensEnhancements(),
          labOrder.lens.billingOptionCodes) : '') +
      (!UtilsService.isPrivateOrder(labOrder) ? this.getMiscellaneousCodesDescriptions(labOrder.lens) : '');

    let rightTableWidth = '25%';
    if ((edge && edge.length > 0)) {
      rightTableWidth = 'auto';
    }

    const thicknessSection = [
      {text: '', alignment: 'right'},
      {text: '', alignment: 'left', bold: true}
    ];
    if (thickness && thickness.length > 0) {
      thicknessSection[0].text = 'Thickness';
      thicknessSection[1].text = thickness;
    }

    const nosePadLabel = 'Nose Pads';
    const nosePadValue = labOrder.lens.nosePads.description !== undefined ? 'Yes (' + labOrder.lens.nosePads.description + ')' : '';
    let nosePadSection = [{}, {}];

    if (this.hasNosePadOption(labOrder.lens.nosePads)) {
      nosePadSection = [
        {text: nosePadLabel, alignment: 'right'},
        {text: nosePadValue, alignment: 'left', bold: true},
      ];
    }
    return [
      this.buildSectionTitle('Miscellaneous/Coatings'),
      {text: summary, alignment: 'left', bold: true},
      {
        columns: [
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['auto', '*'],
              body: [
                [
                  {text: 'Bevel', alignment: 'right'},
                  {text: bevel, alignment: 'left', bold: true},
                ],
                thicknessSection,
                nosePadSection
              ]
            }
          },
          {
            width: '50%',
            layout: 'noBorders',
            table: {
              headerRows: 0,
              widths: ['*', rightTableWidth],
              body: [
                [
                  {text: 'Edge', alignment: 'right'},
                  {text: edge, alignment: 'left', bold: true},
                ]
              ],
            }
          }
        ],
        columnGap: 10
      },
    ];
  }

  getMiscellaneousCodesDescriptions(lens: Lens): string {
    let result = '';
    result += (lens.nosePads && lens.nosePads.description) ? lens.nosePads.codes[0] + '-' + lens.nosePads.description + '; ' : '';
    result += (lens.pressOnPrism && lens.pressOnPrism.description) ? lens.pressOnPrism.codes[0] + '-' +
      lens.pressOnPrism.description + '; ' : '';
    result += (lens.slabOff && lens.slabOff.description) ? lens.slabOff.codes[0] + '-' +  lens.slabOff.description + '.' : '';
    return result;
  }

  hasNosePadOption(nosePads: Coating) {
    let returnValue = false;
    if (nosePads && nosePads.codes) {
      nosePads.codes.forEach(code => {
        if (code.trim().length > 0) {
          returnValue = true;
        }
      });
    }
    return returnValue;
  }

  buildReportTitle(labOrder: LabOrder) {
    return UtilsService.isPrivateOrder(labOrder) ?
      'Lab Order' : 'VSP Contract Lab Order';
  }

  buildSectionTitle(title: string) {
    let sectionTitle;
    if (!title || title.trim().length === 0) {
      sectionTitle = [
        this.buildLineSeparator(),
        this.buildSpacerLineBottom(),
      ];
    } else {
      sectionTitle = [
        this.buildSpacerLine(),
        this.buildLineSeparator(),
        {text: title, alignment: 'center', margin: [0, 2, 0, 3]},
        this.buildLineSeparator(),
        this.buildSpacerLineBottom(),
      ];
    }
    return sectionTitle;
  }

  buildSectionTitleMulti(title1: string, title2: string) {
    return [
      this.buildSpacerLine(),
      this.buildLineSeparator(),
      {
        columns: [
          {width: '50%', text: title1, alignment: 'center', margin: [0, 3, 0, 3]},
          {width: '50%', text: title2, alignment: 'center', margin: [0, 3, 0, 3]}
        ]
      },
      this.buildLineSeparator(),
      this.buildSpacerLineBottom(),
    ];
  }

  buildSpacerLine() {
    return [
      {text: ' ', alignment: 'center', margin: [0, 2, 0, 2], fontSize: 2}
    ];
  }

  buildSpacerLineBottom() {
    return [
      {text: ' ', alignment: 'center', margin: [0, 2, 0, -3], fontSize: 2}
    ];
  }

  buildLineSeparator() {
    return {
      table: {
        headerRows: 0,
        widths: ['100%'],
        body: [
          [{
            text: '',
          }
          ],
        ]
      },
      // https://github.com/bpampuch/pdfmake/issues/218
      layout: {
        paddingLeft: function (i, node) {
          return 0;
        },
        paddingRight: function (i, node) {
          return 0;
        },
        paddingTop: function (i, node) {
          return 0;
        },
        paddingBottom: function (i, node) {
          return 0;
        },
        hLineWidth: function (i, node) {
          return i;
        },
        hLineColor: function (i, node) {
          return 'black';
        },
        vLineWidth: function (i, node) {
          return 0;
        },
      }
    };
  }

  buildDate(dateString) {
    try {
      const pieces = dateString.split('-');
      const newDate =
        (pieces[1].length < 2 ? '0' + pieces[1] : pieces[1]) + '/' +
        (pieces[2].length < 2 ? '0' + pieces[2] : pieces[2]) + '/' +
        pieces[0];
      return newDate;
    } catch (e) {
      return dateString;
    }
  }

  buildFooterTimestamp() {
    const newDate = new Date();
    const year = newDate.getFullYear();
    const month = (newDate.getMonth() + 1) < 10 ? '0' + (newDate.getMonth() + 1) : (newDate.getMonth() + 1).toString();
    const day = (newDate.getDate()) < 10 ? '0' + (newDate.getDate()) : (newDate.getDate()).toString();
    const hours = (newDate.getHours()) < 10 ? '0' + (newDate.getHours()) : (newDate.getHours()).toString();
    const minutes = (newDate.getMinutes()) < 10 ? '0' + (newDate.getMinutes()) : (newDate.getMinutes()).toString();

    return year + ' ' +
      month + ' ' +
      day + ' - ' +
      hours + ':' +
      minutes;
  }

  stripUnderscores(str: string) {
    if (!str || str.length < 1) {
      return str;
    }
    return str.replace(/_/g, ' ');
  }

  zeroPadValueToTenths(valueStr: string) {
    if (!valueStr) {
      return valueStr;
    }

    if (valueStr.lastIndexOf('.') < 0) {
      valueStr = valueStr + '.0';
    }

    return valueStr;
  }

  zeroPadValueToHundredths(valueStr: string) {
    if (!valueStr) {
      return valueStr;
    }

    if (valueStr.lastIndexOf('.') < 0) {
      valueStr = valueStr + '.00';
    }

    const indexFromLast = valueStr.length - 1 - valueStr.lastIndexOf('.');
    if (indexFromLast === 1) {
      valueStr = valueStr + '0';
    }

    return valueStr;
  }

  notUndef(val: any) {
    return val ? val : '';
  }

}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-prod-build/src/app/shared/print-pdf.service.ts